import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";
import { invitacionSuccess, invitacionFailure } from "./invitacionSlice";

/**
 * Crear solicitud de invitacion a un evento
 * POST /invitaciones/solicitar
 */
export const crearSolicitudInvitacion = createAsyncThunk(
  "invitacion/crearSolicitudInvitacion",
  async ({ uidEvento, idTipoTicketInvitacion, ambassadorReferralCode }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.post(
        "/invitaciones/solicitar",
        { uidEvento, idTipoTicketInvitacion, ambassadorReferralCode },
        { headers: { "Content-Type": "application/json" } }
      );
      dispatch(invitacionSuccess({ miSolicitudEnEvento: data }));
      return data;
    } catch (err) {
      const data = err.response?.data;
      return rejectWithValue(
        data
          ? { ...data, status: err.response.status }
          : { message: "Error al solicitar invitacion", status: err.response?.status }
      );
    }
  }
);

/**
 * Obtener la solicitud de invitacion del usuario autenticado para un evento especifico
 * GET /invitaciones/usuario/:uid?uidEvento=X&tipo=activas&page=1&limit=1
 * Guarda el primer resultado en miSolicitudEnEvento (o null si no hay)
 */
export const getMiSolicitudEnEvento = createAsyncThunk(
  "invitacion/getMiSolicitudEnEvento",
  async ({ uidEvento }, { dispatch, getState, rejectWithValue }) => {
    try {
      const uid = getState().auth.user?.uid;
      if (!uid) return rejectWithValue("UID de usuario no disponible");
      const { data } = await api.get(`/invitaciones/usuario/${uid}`, {
        params: { uidEvento, page: 1, limit: 1 },
      });
      const items = data.invitaciones || data || [];
      const solicitud = Array.isArray(items) ? (items[0] || null) : null;
      dispatch(invitacionSuccess({ miSolicitudEnEvento: solicitud }));
      return solicitud;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.response?.data?.message || "Error al obtener solicitud del evento"
      );
    }
  }
);

/**
 * Obtener invitaciones de un usuario paginadas
 * GET /invitaciones/usuario/:uid?tipo=activas|rechazadas|pasadas&page=X&limit=X
 */
export const getInvitacionesDeUsuario = createAsyncThunk(
  "invitacion/getInvitacionesDeUsuario",
  async ({ tipo, page = 1, limit = 10 }, { dispatch, getState, rejectWithValue }) => {
    try {
      const uid = getState().auth.user?.uid;
      if (!uid) return rejectWithValue("UID de usuario no disponible");
      const { data } = await api.get(`/invitaciones/usuario/${uid}`, {
        params: { seccion: tipo, page, limit },
        headers: { "Content-Type": "application/json" },
      });

      const isFirstPage = page === 1;
      const items = data.items || [];
      const hasNextPage = (data.page * data.limit) < data.total;
      const currentPage = data.page || page;

      const paginacionPayload = {
        data: items,
        hasNextPage,
        currentPage,
        isFirstPage,
      };

      if (tipo === "activas") {
        dispatch(invitacionSuccess({ invitacionesActivas: paginacionPayload }));
      } else if (tipo === "rechazadas") {
        dispatch(invitacionSuccess({ invitacionesRechazadas: paginacionPayload }));
      } else if (tipo === "pasadas") {
        dispatch(invitacionSuccess({ invitacionesPasadas: paginacionPayload }));
      }

      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.response?.data?.message || "Error al obtener invitaciones"
      );
    }
  }
);

/**
 * Obtener detalle de una invitacion por UID
 * GET /invitaciones/:uid
 */
export const getInvitacionPorUid = createAsyncThunk(
  "invitacion/getInvitacionPorUid",
  async ({ uid }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.get(`/invitaciones/${uid}`, {
        headers: { "Content-Type": "application/json" },
      });
      dispatch(invitacionSuccess({ invitacionDetalle: data }));
      return data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.response?.data || "Error al obtener invitacion";
      const errorStatus = err.response?.status || null;
      dispatch(invitacionFailure({ message: errorMessage, status: errorStatus }));
      return rejectWithValue({ message: errorMessage, status: errorStatus });
    }
  }
);

/**
 * Obtener solicitudes de un evento (para la empresa organizadora)
 * GET /invitaciones/evento/:uidEvento
 */
export const getSolicitudesDeEvento = createAsyncThunk(
  "invitacion/getSolicitudesDeEvento",
  async ({ uidEvento }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(invitacionSuccess({ solicitudesEventoLoading: true }));
      const { data } = await api.get(`/invitaciones/evento/${uidEvento}`, {
        headers: { "Content-Type": "application/json" },
      });
      dispatch(
        invitacionSuccess({
          solicitudesEvento: data.solicitudes || data,
          solicitudesEventoLoading: false,
        })
      );
      return data;
    } catch (err) {
      dispatch(invitacionSuccess({ solicitudesEventoLoading: false }));
      return rejectWithValue(
        err.response?.data || err.response?.data?.message || "Error al obtener solicitudes del evento"
      );
    }
  }
);

/**
 * Responder solicitudes de invitacion en bloque
 * PUT /invitaciones/responder-bulk
 */
export const responderSolicitudesBulk = createAsyncThunk(
  "invitacion/responderSolicitudesBulk",
  async ({ uidEvento, decisiones }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(
        "/invitaciones/responder-bulk",
        { uidEvento, decisiones },
        { headers: { "Content-Type": "application/json" } }
      );
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.response?.data?.message || "Error al responder solicitudes"
      );
    }
  }
);

/**
 * Iniciar pago de una invitacion
 * POST /invitaciones/:uid/iniciar-pago
 */
export const iniciarPagoInvitacion = createAsyncThunk(
  "invitacion/iniciarPagoInvitacion",
  async ({ uid }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/invitaciones/${uid}/iniciar-pago`,
        {},
        { headers: { "Content-Type": "application/json" } }
      );
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.response?.data?.message || "Error al iniciar pago de invitacion"
      );
    }
  }
);
